<template>
	<div class="round-spider-chart">
		<svg v-if="actorsList.length > 0" :viewBox="`0 0 ${totalSize} ${totalSize}`" class="spider-svg">
			<!-- Background circles -->
			<g class="grid-circles">
				<circle
					v-for="(radius, idx) in gridRadii"
					:key="idx"
					:cx="centerX"
					:cy="centerY"
					:r="radius"
					fill="none"
					stroke="var(--gris-100)"
					stroke-width="1"
				/>
			</g>

			<!-- Axis lines -->
			<g class="axis-lines">
				<line
					v-for="(actor, idx) in actorsList"
					:key="actorKey(actor, idx)"
					:x1="centerX"
					:y1="centerY"
					:x2="getAxisEndPoint(idx).x"
					:y2="getAxisEndPoint(idx).y"
					stroke="var(--gris-100)"
					stroke-width="1"
				/>
			</g>

			<!-- Grid circle labels -->
			<g class="grid-labels">
				<g v-for="(radius, idx) in gridRadii" :key="`grid-label-${idx}`">
					<rect
						:x="centerX - 16"
						:y="centerY - radius - 7"
						width="32"
						height="16"
						fill="var(--blanc)"
						stroke="none"
					/>
					<text
						:x="centerX"
						:y="centerY - radius + 2"
						text-anchor="middle"
						dominant-baseline="central"
						class="grid-label"
						:style="{ fontSize: '10px', fill: 'var(--gris-300)', fontWeight: '600' }"
					>
						{{ Math.round((radius / maxRadius) * 100) }}%
					</text>
				</g>
			</g>

			<!-- Actor labels -->
			<g class="actor-labels">
				<text
					v-for="(actor, idx) in actorsList"
					:key="`label-${actorKey(actor, idx)}`"
					:x="getLabelPosition(idx).x + Math.cos((idx * 2 * Math.PI) / actorsList.length - Math.PI / 2) * labelOffset"
					:y="getLabelPosition(idx).y + Math.sin((idx * 2 * Math.PI) / actorsList.length - Math.PI / 2) * labelOffset"
					text-anchor="middle"
					class="actor-label"
					:style="{ fontSize: actorLabelFontSize, fill: 'var(--gris-600)' }"
				>
					{{ truncateName(getActorName(actor)) }}
				</text>
			</g>

			<!-- Previous comparison polygon (optional, hidden when showPreviousPolygon=false) -->
			<g class="previous-polygon" v-if="showPreviousPolygon && previousScoresNormalized.length === actorsList.length">
				<polygon
					:points="getPolygonPoints(previousScoresNormalized)"
					:fill="chartColor"
					:fill-opacity="hoveredPrevious ? 0.3 : 0.15"
					:stroke="chartColor"
					stroke-width="1"
					stroke-dasharray="6,8"
					:style="{ strokeOpacity: hoveredPrevious ? 0.8 : 0.65, transition: 'all 0.2s ease' }"
					@mouseenter="onPreviousPolygonEnter"
					@mousemove="updateTooltipPosition"
					@mouseleave="onPreviousPolygonLeave"
				/>
			</g>

			<!-- Current data polygon -->
			<g class="data-polygon">
				<polygon
					:points="getPolygonPoints(animatedScores.length === actorsList.length ? animatedScores : scoresNormalized)"
					:fill="chartColor"
					:fill-opacity="hoveredCurrent ? 0.7 : 0.5"
					:stroke="chartColor"
					stroke-width="1.5"
					:style="{ strokeOpacity: 1, transition: 'all 0.2s ease' }"
					@mouseenter="onCurrentPolygonEnter"
					@mousemove="updateTooltipPosition"
					@mouseleave="onCurrentPolygonLeave"
				/>
			</g>

			<!-- Previous data points -->
			<g
				class="previous-data-points"
				v-if="showPreviousPolygon && previousScoresNormalized.length === actorsList.length"
			>
				<circle
					v-for="(point, idx) in getDataPoints(previousScoresNormalized)"
					:key="`prev-point-${idx}`"
					:cx="point.x"
					:cy="point.y"
					:r="3 * pointSize"
					:fill="chartColor"
					stroke="white"
					:stroke-width="pointSize"
					:style="{ opacity: hoveredPrevious ? 0.8 : 0.5, transition: 'all 0.2s ease' }"
				/>
			</g>

			<!-- Current data points -->
			<g class="data-points">
				<circle
					v-for="(point, idx) in getDataPoints(animatedScores.length === actorsList.length ? animatedScores : scoresNormalized)"
					:key="`point-${idx}`"
					:cx="point.x"
					:cy="point.y"
					:r="4 * pointSize"
					:fill="chartColor"
					stroke="white"
					:stroke-width="2 * pointSize"
					:style="{ opacity: hoveredCurrent ? 1 : 0.9, transition: 'all 0.2s ease' }"
				/>
			</g>
		</svg>
		<div v-else class="round-spider-chart-empty">No data</div>

		<!-- Current tooltip (RoundSpiderGraphs-style) -->
		<div v-if="showTooltip && tooltip.current.visible" class="spider-tooltip current-tooltip" :style="{ left: tooltip.current.x + 'px', top: tooltip.current.y + 'px' }">
			<div class="tooltip-header">
				<p>{{ tooltip.current.itemName }} - {{ t('current') }}</p>
			</div>
			<div class="tooltip-content">
				<div v-for="row in tooltip.current.data" :key="row.actorId" class="tooltip-row current">
					<span class="actor-name">{{ row.actorName }}</span>
					<span class="score">{{ Math.round(row.score) }}%</span>
					<ComparatorValue v-if="hasPreviousData" :value="Math.round(row.score)" :comparated="Math.round(row.previousScore)" class="comparator" />
				</div>
			</div>
		</div>

		<!-- Previous tooltip (when hasPreviousData and polygon visible) -->
		<div v-if="showTooltip && showPreviousPolygon && tooltip.previous.visible" class="spider-tooltip last-analysis-tooltip" :style="{ left: tooltip.previous.x + 'px', top: tooltip.previous.y + 'px' }">
			<div class="tooltip-header">
				<p>{{ tooltip.previous.itemName }} - {{ t('previous') }}</p>
			</div>
			<div class="tooltip-content">
				<div v-for="row in tooltip.previous.data" :key="row.actorId" class="tooltip-row">
					<span class="actor-name">{{ row.actorName }}</span>
					<span class="score">{{ Math.round(row.score) }}%</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ComparatorValue from '@/components/dashboard/comparatorValue.vue';

const { t } = useI18n();
const emit = defineEmits(['polygon-enter', 'polygon-leave']);

const props = defineProps({
	actors: {
		type: Array,
		default: () => [],
	},
	scores: {
		type: Array,
		default: () => [],
	},
	previousScores: {
		type: Array,
		default: () => [],
	},
	color: {
		type: String,
		default: '#0EA5A1',
	},
	size: {
		type: Number,
		default: 330,
	},
	itemName: {
		type: String,
		default: '',
	},
	showPreviousPolygon: {
		type: Boolean,
		default: true,
	},
	showTooltip: {
		type: Boolean,
		default: true,
	},
	pointSize: {
		type: Number,
		default: 1,
	},
});

const hoveredCurrent = ref(false);
const hoveredPrevious = ref(false);

const tooltip = ref({
	current: { visible: false, x: 0, y: 0, itemName: '', data: [] },
	previous: { visible: false, x: 0, y: 0, itemName: '', data: [] },
});

const calculateTooltipPosition = (event, type) => {
	const baseX = event.clientX + 15;
	const baseY = event.clientY - 10;
	const bothVisible = tooltip.value.current.visible && tooltip.value.previous.visible;
	const tooltipWidth = 200;
	const spacing = 10;
	if (bothVisible && type === 'previous') {
		return { x: baseX + tooltipWidth + spacing, y: baseY };
	}
	return { x: baseX, y: baseY };
};

const showCurrentTooltip = event => {
	const data = actorsList.value.map((actor, idx) => ({
		actorId: actorKey(actor, idx),
		actorName: String(getActorName(actor)).charAt(0).toUpperCase() + String(getActorName(actor)).slice(1),
		score: Number(props.scores?.[idx] ?? 0),
		previousScore: Number(props.previousScores?.[idx] ?? 0),
	}));
	const pos = calculateTooltipPosition(event, 'current');
	tooltip.value.current = { visible: true, x: pos.x, y: pos.y, itemName: props.itemName, data };
};

const showPreviousTooltip = event => {
	const data = actorsList.value.map((actor, idx) => ({
		actorId: actorKey(actor, idx),
		actorName: String(getActorName(actor)).charAt(0).toUpperCase() + String(getActorName(actor)).slice(1),
		score: Number(props.previousScores?.[idx] ?? 0),
	}));
	const pos = calculateTooltipPosition(event, 'previous');
	tooltip.value.previous = { visible: true, x: pos.x, y: pos.y, itemName: props.itemName, data };
};

const hideCurrentTooltip = () => {
	tooltip.value.current.visible = false;
};

const hidePreviousTooltip = () => {
	tooltip.value.previous.visible = false;
};

const updateTooltipPosition = event => {
	if (tooltip.value.current.visible) {
		const pos = calculateTooltipPosition(event, 'current');
		tooltip.value.current.x = pos.x;
		tooltip.value.current.y = pos.y;
	}
	if (tooltip.value.previous.visible) {
		const pos = calculateTooltipPosition(event, 'previous');
		tooltip.value.previous.x = pos.x;
		tooltip.value.previous.y = pos.y;
	}
};

const onCurrentPolygonEnter = e => {
	hoveredCurrent.value = true;
	emit('polygon-enter');
	if (props.showTooltip) showCurrentTooltip(e);
};
const onCurrentPolygonLeave = () => {
	hoveredCurrent.value = false;
	emit('polygon-leave');
	if (props.showTooltip) hideCurrentTooltip();
};
const onPreviousPolygonEnter = e => {
	hoveredPrevious.value = true;
	if (props.showTooltip) showPreviousTooltip(e);
};
const onPreviousPolygonLeave = () => {
	hoveredPrevious.value = false;
	if (props.showTooltip) hidePreviousTooltip();
};

const padding = 20;
const maxRadius = 100;
const gridRadii = [20, 40, 60, 80, 100];

const actorsList = computed(() => props.actors || []);
const totalSize = computed(() => chartSize.value + padding * 2);
const centerX = computed(() => totalSize.value / 2);
const centerY = computed(() => totalSize.value / 2);
const chartColor = computed(() => props.color);
const pointSize = computed(() => Math.max(0, Math.min(3, Number(props.pointSize) ?? 1)));

const chartSize = computed(() => props.size || 330);
const scoresNormalized = computed(() => {
	const raw = props.scores || [];
	return raw.map(s => Math.min(100, Math.max(0, Number(s || 0))) / 100);
});

const TRANSITION_DURATION = 400;
const animatedScores = ref([]);
let animationFrameId = null;

watch(
	scoresNormalized,
	(newScores, oldScores) => {
		if (animationFrameId) cancelAnimationFrame(animationFrameId);
		if (!newScores?.length || newScores.length !== actorsList.value.length) {
			animatedScores.value = [...(newScores || [])];
			return;
		}
		const current = animatedScores.value;
		const canAnimate = current.length === newScores.length && oldScores !== undefined;
		const startScores = canAnimate ? current : newScores;
		if (!canAnimate) {
			animatedScores.value = [...newScores];
			return;
		}
		const startTime = performance.now();

		const tick = now => {
			const elapsed = now - startTime;
			const t = Math.min(1, elapsed / TRANSITION_DURATION);
			const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
			const next = startScores.map((s, i) => s + ((newScores[i] ?? 0) - s) * eased);
			animatedScores.value = next;
			if (t < 1) animationFrameId = requestAnimationFrame(tick);
		};
		animationFrameId = requestAnimationFrame(tick);
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	if (animationFrameId) cancelAnimationFrame(animationFrameId);
});

const previousScoresNormalized = computed(() => {
	const raw = props.previousScores || [];
	return raw.map(s => Math.min(100, Math.max(0, Number(s || 0))) / 100);
});

const hasPreviousData = computed(
	() => previousScoresNormalized.value.length === actorsList.value.length && actorsList.value.length > 0,
);
const showPreviousPolygon = computed(
	() => hasPreviousData.value && props.showPreviousPolygon,
);

const actorKey = (actor, idx) => {
	const id = actor?.id ?? actor?.actor_id ?? idx;
	return id;
};

const getActorName = actor => actor?.name ?? '';

const actorLabelFontSize = computed(() => {
	const n = actorsList.value.length;
	if (n <= 5) return '14px';
	if (n <= 8) return '12px';
	if (n <= 12) return '10px';
	return '8px';
});

const labelOffset = computed(() => {
	const n = actorsList.value.length;
	if (n <= 5) return 16;
	if (n <= 8) return 20;
	if (n <= 12) return 24;
	return 28;
});

const truncateName = (name, maxLen = null) => {
	const capitalized = String(name || '').trim()
		? String(name || '').charAt(0).toUpperCase() + String(name || '').slice(1)
		: '';
	if (!maxLen) {
		const n = actorsList.value.length;
		if (n <= 5) maxLen = 15;
		else if (n <= 8) maxLen = 12;
		else if (n <= 12) maxLen = 10;
		else maxLen = 8;
	}
	return capitalized.length > maxLen ? capitalized.substring(0, maxLen) + '...' : capitalized;
};

const getAxisEndPoint = idx => {
	const angle = (idx * 2 * Math.PI) / actorsList.value.length - Math.PI / 2;
	return {
		x: centerX.value + Math.cos(angle) * maxRadius,
		y: centerY.value + Math.sin(angle) * maxRadius,
	};
};

const getLabelPosition = idx => {
	const angle = (idx * 2 * Math.PI) / actorsList.value.length - Math.PI / 2;
	const normalizedAngle = ((angle + Math.PI / 2) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
	const isHorizontal =
		normalizedAngle < Math.PI / 4 ||
		normalizedAngle > (7 * Math.PI) / 4 ||
		(normalizedAngle > (3 * Math.PI) / 4 && normalizedAngle < (5 * Math.PI) / 4);
	const labelRadius = maxRadius + (isHorizontal ? 12 : 20);
	return {
		x: centerX.value + Math.cos(angle) * labelRadius,
		y: centerY.value + Math.sin(angle) * labelRadius + 5,
	};
};

const getPolygonPoints = normalizedScores => {
	const points = [];
	actorsList.value.forEach((_, idx) => {
		const score = normalizedScores[idx] ?? 0;
		const angle = (idx * 2 * Math.PI) / actorsList.value.length - Math.PI / 2;
		const radius = score * maxRadius;
		const x = centerX.value + Math.cos(angle) * radius;
		const y = centerY.value + Math.sin(angle) * radius;
		points.push(`${x},${y}`);
	});
	return points.join(' ');
};

const getDataPoints = normalizedScores => {
	const points = [];
	actorsList.value.forEach((_, idx) => {
		const score = normalizedScores[idx] ?? 0;
		const angle = (idx * 2 * Math.PI) / actorsList.value.length - Math.PI / 2;
		const radius = score * maxRadius;
		points.push({
			x: centerX.value + Math.cos(angle) * radius,
			y: centerY.value + Math.sin(angle) * radius,
		});
	});
	return points;
};
</script>

<style lang="scss" scoped>
.round-spider-chart {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;

	.spider-svg {
		width: 100%;
		height: 100%;
		min-width: 0;
		min-height: 0;

		.actor-label {
			font-weight: 500;
		}

		.grid-label {
			user-select: none;
		}

		.previous-polygon polygon {
			cursor: pointer;
			&:hover {
				stroke-width: 2px;
			}
		}

		.data-polygon polygon {
			cursor: pointer;
			&:hover {
				filter: brightness(0.92);
				stroke-width: 2px;
			}
		}
	}

	.round-spider-chart-empty {
		font-size: 0.9rem;
		color: var(--gris-500);
		font-style: italic;
	}

	.spider-tooltip {
		position: fixed;
		z-index: 9999;
		background: var(--blanc);
		border: 1px solid var(--gris-100);
		border-radius: 8px;
		padding: 0;
		min-width: 200px;
		pointer-events: none;
		font-size: 14px;

		&.last-analysis-tooltip {
			border: 2px dashed var(--gris-300);
			.tooltip-header {
				background: var(--gris-100);
				p { color: var(--gris-700); }
			}
			.tooltip-content .tooltip-row {
				.actor-name, .score { color: var(--gris-600); }
			}
		}

		.tooltip-header {
			background: var(--gris-50);
			padding: 8px 12px;
			border-bottom: 1px solid var(--gris-100);
			border-radius: 8px 8px 0 0;
			p { margin: 0; font-size: 14px; font-weight: 600; color: var(--noir); }
		}

		.tooltip-content {
			padding: 0.6rem 1.2rem;
			.tooltip-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 0.2rem 0;
				&.current {
					display: grid;
					grid-template-columns: 1fr 0.5fr 1fr;
					column-gap: 1rem;
					.comparator { justify-self: start; }
				}
				.actor-name {
					color: var(--noir);
					font-weight: 500;
					min-width: 0;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				.score { color: var(--noir); }
			}
		}
	}
}
</style>